import { DomNodes } from "../../constants"
import parse, {
  Element as ParseElement,
  attributesToProps,
  domToReact
} from "html-react-parser"
import { styled } from "@mui/material"
import { forwardRef, isValidElement, useEffect, useRef, useState } from "react"
import { useSnapshot } from "valtio"
import { CurrentTestState, ExtractedImagesState } from "../../storage"
import { ImageMagnifier } from "./imageMagnifier"

export interface PackagedImageProps {
  itemId: string
  resourceId: string
  componentNode: DomNodes
  src: string
  alt?: string
  fullWidth?: boolean
  className?: string
  style?: React.CSSProperties
  tabIndex?: number
  allowZoom?: boolean
  accessibilityAttr?: { [key: string]: string }
  width: string
  // a11y: app-supplied resolver for the live Media Library alt (Ready#7894/#7937) — given the
  // asset id or the image src, returns the live altText (or undefined if unresolved).
  resolveAlt?: (assetIdOrSrc: string) => string | undefined
}

const MIME_TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  svg: "image/svg+xml"
}

const PackagedImageBase = forwardRef<any, PackagedImageProps>((props, ref) => {
  const {
    src,
    alt = "",
    itemId,
    resourceId,
    componentNode,
    fullWidth,
    allowZoom = true,
    accessibilityAttr,
    width,
    resolveAlt,
    ...imageProps
  } = props
  const imageRef = useRef<HTMLImageElement>(null)
  const { images } = useSnapshot(ExtractedImagesState)
  const [svgData, setSvgData] = useState("")
  const [source, setSource] = useState("")
  const [loaded, setLoaded] = useState(false)
  const [cropBox, setCropBox] = useState({})

  const cropProps = imageProps["data-crop-props"]

  // a11y: a non-empty inline alt wins; otherwise fall back to the live Media Library alt the
  // app resolves from the asset id (or src) — an empty inline alt no longer renders empty.
  const assetId = (imageProps as { id?: string }).id
  const resolvedAlt = alt.trim() ? alt : resolveAlt?.(assetId ?? src) ?? ""

  useEffect(() => {
    if (src?.includes("http") || src?.includes("base64")) {
      setSource(src)
      return
    }

    const imageResourceId =
      resourceId || CurrentTestState.currentTest.resourceId

    if (images.length && src) {
      const rootSrc = src.includes("../") ? src.replaceAll("../", "") : src
      const image = ExtractedImagesState.images.find(
        image =>
          image.resourceIds.some(resId => resId === imageResourceId) &&
          image.path.includes(rootSrc)
      )

      if (!image) {
        return
      }
      const extension = src.split(".").pop()
      const mimeType =
        MIME_TYPES[extension as keyof typeof MIME_TYPES] || MIME_TYPES.jpeg
      if (mimeType === MIME_TYPES.svg) {
        setSvgData(image.imageData)
        return
      }
      setSource(`data:${mimeType};base64,${image.imageData}`)
    }
  }, [images, images.length, resourceId, src])

  useEffect(() => {
    if (!cropProps) return
    if (!loaded) return

    if (!imageRef.current) return

    const { naturalWidth, naturalHeight } = imageRef.current
    const [width, height, x, y, scaleX, scaleY, rotate] = (
      cropProps as string
    ).split("/")

    const newWidth = (naturalWidth / Number(width)) * 200
    const newHeight = (naturalHeight / Number(height)) * 200

    const translateX = (newWidth / naturalWidth) * Number(x)
    const translateY = (newHeight / naturalHeight) * Number(y)

    setCropBox({
      width: newWidth,
      height: newHeight,
      transform: `translateX(${
        translateX > 0 ? `-${translateX}` : `${Math.abs(translateX)}`
      }px) translateY(${
        translateY > 0 ? `-${translateY}` : `${Math.abs(translateY)}`
      }px) rotate(${rotate}deg) scaleX(${scaleX}) scaleY(${scaleY})`
    })
  }, [cropProps, loaded])

  const ImageComponent = allowZoom ? ImageMagnifier : "img"

  const cleanSvgData = (svg: string) => {
    if (!svg) return

    const parsedSvg = parse(svg, {
      replace: domNode => {
        const element = domNode as ParseElement

        // Apply className and styles from image tag to parsed SVG.
        if (element.name === "svg") {
          const props = attributesToProps(element.attribs)

          return (
            <svg
              {...props}
              style={{ ...props.style, ...imageProps.style }}
              className={`${imageProps.className ?? ""} ${
                props.className ?? ""
              }`}
            >
              {domToReact(element.children)}
            </svg>
          )
        }
      }
    })

    if (!Array.isArray(parsedSvg)) return

    return parsedSvg.filter(item => isValidElement(item))
  }

  return (
    <>
      {svgData ? (
        allowZoom ? (
          <ImageMagnifier ref={ref}>{cleanSvgData(svgData)}</ImageMagnifier>
        ) : (
          cleanSvgData(svgData)
        )
      ) : null}
      {source && (
        <ImageComponent
          ref={imageRef}
          src={source}
          alt={resolvedAlt}
          style={cropBox ?? {}}
          {...imageProps}
          onLoad={() => setLoaded(true)}
        />
      )}
    </>
  )
})

export const PackagedImage = styled(PackagedImageBase)(
  ({ fullWidth = true, width }) =>
    fullWidth
      ? {
          height: "auto",
          margin: "0 !important",
          width: width ? `${width}px` : undefined,
          maxWidth: width ? `${width}px` : "100%"
        }
      : {}
)
